import { Component, inject, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import { PromanButtonComponent } from '@proman/button';
import { PromanChartsModule } from '@proman/charts/charts.module';
import { PromanCommonComponentsModule } from '@proman/common-components/proman-common-components.module';
import { date } from '@proman/interfaces/common.interface';
import {
    Assets,
    Employee,
    EmployeeContract,
    EmployeeScheduleEntry,
    EmployeeWorkshift,
    Material,
    PromanFile,
    Template,
    Vacations,
    WorkdayExceptionReason,
} from '@proman/interfaces/entity-interfaces';
import { CurrUser, EntityItemFieldConfig, TableConfig } from '@proman/interfaces/object-interfaces';
import { CalendarEntityInterface } from '@proman/resources/calendar';
import { employee } from '@proman/resources/employee';
import { WorkshiftEntityInterface } from '@proman/resources/workshift';
import { ACL } from '@proman/services/acl.service';
import { Entity, EntityInterface } from '@proman/services/entity.service';
import { FilePreviewService } from '@proman/services/file-preview.service';
import { FilterService } from '@proman/services/filter.service';
import { QueryExpressionService } from '@proman/services/query-expression.service';
import { SmartsignService } from '@proman/services/smartsign.service';
import { ToastService } from '@proman/services/toast.service';
import { TranslatePipe } from '@proman/shared/pipes/pipes/translate.pipe';
import { InputDialogComponent } from '@proman/shared-dialogs/dialogs/input-dialog.component';
import { TextPreviewDialogComponent } from '@proman/shared-dialogs/dialogs/text-preview-dialog.component';
import { getCurrUser } from '@proman/store/curr-user';
import { PromanTableModule } from '@proman/table';
import { cloneDeep, findById } from '@proman/utils';
import moment from 'moment';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { forkJoin as observableForkJoin } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';

import { EmployeesService } from '../../employees/services/employees.service';
import { SharedComponentsModule } from '../../shared-components/shared-components.module';
import { FrontendDirectivesModule } from '../frontend-directives.module';
import { Dialog } from '../services/dialog.service';

@Component({
    selector: 'pm-person-dashboard',
    template: `
        <pm-toolbar-driver [title]="'personal_dashboard' | translate"></pm-toolbar-driver>
        @if (personEmployee) {
        <mat-tab-group (selectedTabChange)="handleChangeTab($event)">
            <mat-tab [label]="'dashboard' | translate">
                <div fxLayout="row wrap" fxLayout.lt-lg="column" pmPadding>
                    <div fxLayout="column wrap" fxFlex="50">
                        <pm-frame pmPadding id="person-summary-card">
                            <div fxLayout="row" fxLayoutAlign="start center">
                                <pm-user-thumb [thumb]="userPhoto"></pm-user-thumb>
                                <div fxLayout="column">
                                    <div>
                                        <h3>{{ currUser.person.name }}</h3>
                                    </div>
                                    <div fxLayout="row" fxLayoutAlign="start center">
                                        <p>{{ personSpecialisationsString }}</p>
                                        &nbsp;&vert;&nbsp;
                                        <p>
                                            <b>{{ unusedVacationDays }}</b> {{ 'unused_vacations' | translate }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </pm-frame>

                        <pm-frame pmPadding id="personal-schedule-card">
                            @if (ready) {
                            <pm-month-view
                                [getDateColor]="getDayColor"
                                [getDateText]="getDayText"
                                [timeStamp]="timestamp"
                                [config]="{ index: 0 }"
                                (onChange)="checkEntryInfo($event)"
                                (onChangeMonthYear)="onYearMonthChange($event)"
                                fxFlex></pm-month-view>
                            }
                        </pm-frame>

                        <pm-frame pmPadding id="popular-templates-card">
                            <div fxLayout="row wrap">
                                @for (template of personEmployee.templates; track template.id) {
                                <pro-btn
                                    [label]="template.name"
                                    theme="accent"
                                    (onClick)="handleTemplateAction(template)"></pro-btn>
                                }
                            </div>
                        </pm-frame>
                    </div>

                    <div fxLayout="column wrap" fxFlex.gt-md="50">
                        <mat-accordion fxFlex pmPadding>
                            <mat-expansion-panel id="vacation-requests-card">
                                <mat-expansion-panel-header>
                                    <mat-panel-title>{{ 'vacations' | translate }}</mat-panel-title>
                                </mat-expansion-panel-header>

                                <pro-table [config]="vacationRequestsTableConfig" [timeStamp]="timestamp"></pro-table>
                            </mat-expansion-panel>
                            <mat-expansion-panel id="remote-work-documents-card">
                                <mat-expansion-panel-header>
                                    <mat-panel-title>{{ 'remote_work' | translate }}</mat-panel-title>
                                </mat-expansion-panel-header>

                                <pro-table [config]="remoteWorkRequestsTableConfig" [timeStamp]="timestamp"></pro-table>
                            </mat-expansion-panel>

                            <mat-expansion-panel id="personal-contracts-card">
                                <mat-expansion-panel-header>
                                    <mat-panel-title>{{ 'personal_contracts' | translate }}</mat-panel-title>
                                </mat-expansion-panel-header>

                                <pro-table [config]="personalContractsTableConfig" [timeStamp]="timestamp"></pro-table>
                            </mat-expansion-panel>

                            <mat-expansion-panel id="personal-safety-signatures-card">
                                <mat-expansion-panel-header>
                                    <mat-panel-title>{{ 'safety_signatures' | translate }}</mat-panel-title>
                                </mat-expansion-panel-header>

                                <pro-table [config]="safetySignaturesTableConfig" [timeStamp]="timestamp"></pro-table>
                            </mat-expansion-panel>
                        </mat-accordion>

                        <mat-accordion fxFlex pmPadding>
                            <mat-expansion-panel id="asset-assignment-card">
                                <mat-expansion-panel-header>
                                    <mat-panel-title>{{ 'assets' | translate }}</mat-panel-title>
                                </mat-expansion-panel-header>

                                <pro-table [config]="assetTableConfig" [timeStamp]="timestamp"></pro-table>
                            </mat-expansion-panel>

                            <mat-expansion-panel id="inventory-assignment-card">
                                <mat-expansion-panel-header>
                                    <mat-panel-title>{{ 'inventory' | translate }}</mat-panel-title>
                                </mat-expansion-panel-header>

                                @if (ACL.check('material.view')) {
                                <pro-table [config]="inventoryTableConfig" [timeStamp]="timestamp"></pro-table>
                                } @else {
                                <pro-editable-container
                                    >{{ 'no_permissions_for' | translate }}: {{ 'material_view' | translate }}
                                </pro-editable-container>
                                }
                            </mat-expansion-panel>
                        </mat-accordion>
                    </div>
                </div>
            </mat-tab>
            <mat-tab [label]="'charts' | translate">
                @if (allowCharts && contractChartData) {
                <pro-echart-line [data]="contractChartData"></pro-echart-line>
                }
            </mat-tab>
        </mat-tab-group>

        }
    `,
    imports: [
        MatExpansionModule,
        MatTabsModule,
        FlexLayoutModule,
        FrontendDirectivesModule,
        SharedComponentsModule,
        PromanTableModule,
        TranslatePipe,
        PromanCommonComponentsModule,

        PromanButtonComponent,
        PromanChartsModule,
    ],
    styles: [
        `
            mat-accordion {
                max-width: 90vw;
            }
        `,
    ],
})
export class PersonDashboardComponent implements OnInit {
    ACL = inject(ACL);
    Dialog = inject(Dialog);
    Employees = inject(EmployeesService);
    Entity = inject(Entity);
    FilePreview = inject(FilePreviewService);
    Filter = inject(FilterService);
    QueryExpression = inject(QueryExpressionService);
    store = inject(Store);
    Smartsign = inject(SmartsignService);
    Toast = inject(ToastService);

    workshiftEntity: WorkshiftEntityInterface;
    employeeWorkshiftEntity: EntityInterface<EmployeeWorkshift>;
    workdayExceptionReasonEntity: EntityInterface<WorkdayExceptionReason>;
    calendarEntity: CalendarEntityInterface;

    currUser: CurrUser;
    userPhoto: PromanFile;
    personSpecialisationsString: string;
    personEmployee: any; //Employee
    unusedVacationDays: number;
    activeTabIndex: number;
    date: any;

    calendarOptions: any;
    scheduleEntries: any;
    workshifts: any;
    weekdays: any;
    workdayExceptionReasons: WorkdayExceptionReason[];
    timestamp: number;
    ready: boolean;
    allowCharts: boolean;

    vacationRequestsTableConfig: TableConfig;
    remoteWorkRequestsTableConfig: TableConfig;
    personalContractsTableConfig: TableConfig;
    safetySignaturesTableConfig: TableConfig;
    assetTableConfig: TableConfig;
    inventoryTableConfig: TableConfig;

    contractChartData: any[];
    contractChartConfig: any = {
        colors: ['#95B227', '#77B0FF'],
        xFormat: 'text',
        label: { x: 'date', y: 'value' },
    };

    constructor() {
        this.store
            .select(getCurrUser)
            .pipe(take(1))
            .subscribe((user) => {
                this.currUser = user;
                console.log('currUser: ', this.currUser);
            });

        this.workshiftEntity = this.Entity.get('workshift');
        this.employeeWorkshiftEntity = this.Entity.get('employee_workshift');
        this.workdayExceptionReasonEntity = this.Entity.get('workday_exception_reason');
        this.calendarEntity = this.Entity.get('calendar');
        this.date = moment();
    }

    ngOnInit() {
        switch (true) {
            case this.currUser.isEmployee:
                this.Entity.get('employee')
                    .get({ ...employee.params.get, id: this.currUser.person.id })
                    .then((response: Employee) => {
                        console.log('Employee get: ', response);
                        this.personEmployee = response;
                        this.userPhoto = response.photo;
                        this.personSpecialisationsString = response.specialisations.map((spec) => spec.name).join(', ');
                        this.Entity.get('employee')
                            .unusedVacationDaysNow({ id: response.id, end: moment().format() })
                            .then((response) => (this.unusedVacationDays = response));

                        this.init();
                        this.setTableConfigs();
                    });
                break;
        }

        this.weekdays = this.Filter.translate('_day_names').split(' ');
        this.weekdays.push(this.weekdays.shift()); // push Sunday to end
    }

    setTableConfigs() {
        this.vacationRequestsTableConfig = {
            entity: 'vacations',
            addButton: {
                acl: null,
                template: 'create',
                callback: () => this.createVacationRequest(false),
            },
            fields: [
                {
                    name: 'from',
                    key: 'startFrom',
                    formatter: 'dateTime',
                },
                {
                    name: 'duration',
                    key: 'duration',
                    stats: ['sum'],
                },
                {
                    name: 'type',
                    key: 'workdayExceptionReason.name',
                },
                this.Employees.getVacationTableField('unpaid'),
                this.Employees.getVacationTableField('confirmed'),
            ],
            rowButtons: [
                {
                    icon: 'signature-lock',
                    acl: null,
                    theme: 'accent',
                    callback: (row: Vacations) => this.Smartsign.smartsign(row.files[0]),
                    show: (row: Vacations) =>
                        this.personEmployee.id === this.currUser.person.id && row.files.length > 0,
                },
                {
                    icon: 'file',
                    acl: null,
                    callback: (row: Vacations) => {
                        this.FilePreview.show(row.files[0]);
                    },
                    tooltip: 'preview_file',
                    show: (vacation: Vacations) => vacation.files.length > 0,
                },
            ],
            hideSettings: true,
            extraParameters: {
                sort: { startFrom: 'desc' },
                join: ['workdayExceptionReason', 'files'],
                'workdayExceptionReason.type': '!remote_work',
                'employee.id': this.personEmployee.id,
            },
        };

        this.remoteWorkRequestsTableConfig = {
            entity: 'vacations',
            addButton: {
                acl: null,
                template: 'create',
                callback: () => this.createVacationRequest(true),
            },
            fields: [
                {
                    name: 'from',
                    key: 'startFrom',
                    formatter: 'dateTime',
                },
                {
                    name: 'duration',
                    key: 'duration',
                    stats: ['sum'],
                },
            ],
            rowButtons: [
                {
                    icon: 'signature-lock',
                    acl: null,
                    theme: 'accent',
                    callback: (row: Vacations) => this.Smartsign.smartsign(row.files[0]),
                    show: (row: Vacations) =>
                        this.personEmployee.id === this.currUser.person.id && row.files.length > 0,
                },
                {
                    icon: 'file',
                    acl: null,
                    callback: (row: Vacations) => {
                        this.FilePreview.show(row.files[0]);
                    },
                    tooltip: 'preview_file',
                    show: (vacation: Vacations) => vacation.files.length > 0,
                },
            ],
            hideSettings: true,
            extraParameters: {
                sort: { startFrom: 'desc' },
                join: ['workdayExceptionReason', 'files'],
                'workdayExceptionReason.type': this.QueryExpression.eqStrict('remote_work'),
                'employee.id': this.personEmployee.id,
            },
        };

        this.personalContractsTableConfig = {
            entity: 'employee_contract',
            fields: [
                {
                    name: 'contract_number',
                    key: 'number',
                },
                {
                    name: 'change_date',
                    key: 'contractChangeDate',
                    formatter: 'dateTime',
                },
                {
                    name: 'start_date',
                    key: 'startDate',
                    formatter: 'dateTime',
                    hidden: true,
                },
                {
                    name: 'end_date',
                    key: 'endDate',
                    formatter: 'dateTime',
                    hidden: true,
                },
            ],
            rowButtons: [
                {
                    icon: 'signature-lock',
                    acl: null,
                    theme: 'accent',
                    callback: (row: EmployeeContract) => this.Smartsign.smartsign(row.file),
                    show: (row: EmployeeContract) => this.personEmployee.id === this.currUser.person.id && !!row.file,
                },
                {
                    icon: 'file',
                    acl: null,
                    callback: (row: EmployeeContract) => {
                        this.FilePreview.show(row.file);
                    },
                    tooltip: 'preview_file',
                    show: (row: EmployeeContract) => !!row.file,
                },
            ],
            hideSettings: true,
            extraParameters: {
                join: ['file'],
                'employee.id': this.personEmployee.id,
            },
            transform: (data: EmployeeContract[]) => {
                this.contractChartData = [{ key: data[0].duties, values: [] }];
                data.forEach((datum) => this.contractChartData[0].values.push([datum.startDate, datum.brutto]));
                return data;
            },
        };

        this.safetySignaturesTableConfig = {
            entity: 'employee_document_read',
            fields: [
                {
                    name: 'document',
                    key: 'employeeDocument.name',
                },
                {
                    name: 'date',
                    key: 'date',
                    formatter: 'dateTime',
                },
                {
                    name: 'signature',
                    key: 'signature',
                    formatter: 'image',
                },
                {
                    name: 'confirmation',
                    key: 'confirmation',
                    formatter: 'image',
                },
            ],
            hideSettings: true,
            extraParameters: {
                sort: { date: 'desc' },
                join: ['employeeDocument', 'signature', 'confirmation'],
                'employee.id': this.personEmployee.id,
            },
        };

        this.assetTableConfig = {
            entity: 'assets',
            tableId: 'employee_assets',
            aclRoot: 'employee',
            fields: [
                {
                    name: 'name',
                    key: 'name',
                },
                {
                    name: 'alias',
                    key: 'alias',
                },
                {
                    name: 'comments',
                    key: 'comments',
                    hidden: true,
                },
                {
                    name: 'inventory_number',
                    key: 'inventoryNumber',
                },
                {
                    name: 'document_number',
                    key: 'documentNumber',
                },
                {
                    name: 'exploitation_start',
                    key: 'exploitationStart',
                    formatter: 'dateTime',
                },
                {
                    name: 'installation_cost',
                    key: 'installationCost',
                    formatter: 'money',
                },
                {
                    name: 'purchase_cost',
                    key: 'purchaseCost',
                    formatter: 'money',
                    hidden: true,
                },
                {
                    name: 'residual_value',
                    key: 'residualValue',
                    formatter: 'money',
                },
                {
                    name: 'amortization_duration_months',
                    key: 'amortizationDuration',
                    filter: null,
                    sort: false,
                },
                {
                    name: 'include_in_report',
                    key: 'includeInReport',
                    formatter: 'compile',
                    getValue: (row: Assets) => {
                        const value = row.includeInReport;
                        const icon = {
                            icon: value ? 'check' : 'times',
                            color: value ? 'green' : 'grey',
                        };

                        return {
                            icon,
                            text: '',
                        };
                    },
                },
                {
                    name: 'workplace',
                    key: 'workplace.alias',
                    hidden: true,
                },
                {
                    name: 'supplier',
                    key: 'purchase.supplier.name',
                    hidden: true,
                },
            ],
            rowButtons: [
                {
                    icon: 'print',
                    tooltip: 'print_document',
                    acl: null,
                    show: (asset: Assets) => !!asset && !!asset.document && !!asset.document.file,
                    callback: (asset: Assets) => this.FilePreview.show(asset.document.file),
                },
            ],
            extraParameters: {
                'person.id': this.personEmployee.id,
                join: [
                    'workplace',
                    'recordDbit',
                    'recordCrdt',
                    'recordDbit.accountCategory',
                    'recordCrdt.accountCategory',
                    'document',
                    'document.file',
                    'purchase',
                    'purchase.supplier',
                    'person',
                ],
            },
        };

        this.inventoryTableConfig = {
            entity: 'material',
            tableId: 'employee_inventory',
            aclRoot: 'employee',
            fields: [
                {
                    name: 'name',
                    key: 'name',
                },
                {
                    name: 'alias',
                    key: 'alias',
                },
            ],
            rowButtons: [
                {
                    icon: 'print',
                    tooltip: 'print_document',
                    acl: null,
                    show: (inventory: Material) => inventory.stock === 'inventory' && !!(inventory as any).document,
                    callback: (inventory: Material) => this.FilePreview.show((inventory as any).document.file),
                },
            ],
            extraParameters: {
                'person.id': this.personEmployee.id,
                join: ['person', 'document', 'document.file'],
            },
        };
    }

    getDayColor = (day: any) => {
        const entry = this.getEntry(day);
        let color = {
            enabled: true,
            tooltip: null,
            color: null,
        };

        if (entry) {
            if (entry.reason) color = entry.reason.color;
            else if (entry.isChangedTime) color = this.calendarOptions.changedScheduleColor;
            else if (entry.workshift) color = entry.workshift.color;
        }

        return color;
    };

    getEntry = (day: any) => {
        const dateStr = moment(day).format('YYYY-MM-DD');
        return this.scheduleEntries[dateStr];
    };

    init = () => {
        this.scheduleEntries = {};

        return new Promise((resolve) => {
            resolve(
                observableForkJoin([
                    this.Entity.get('workshift').search(),
                    this.workdayExceptionReasonEntity.search(),
                    this.calendarEntity.getOptions(),
                ])
                    .toPromise()
                    .then((values: any) => {
                        this.workshifts = values[0];
                        this.workdayExceptionReasons = values[1];
                        this.calendarOptions = values[2];

                        this.loadEmployeeWorkshifts();
                        this.loadEntries();
                    })
            );
        });
    };

    handleChangeTab(value: any) {
        if (value.index === 1) {
            setTimeout(() => {
                this.allowCharts = true;
            }, 500);
        }
    }

    createVacationRequest = (remoteWork?: boolean, preselectValues?: any) => {
        const parameters: EntityItemFieldConfig[] = [
            {
                name: 'duration',
                key: 'duration',
                type: 'number',
                config: {},
            },
            {
                key: 'template',
                name: 'template',
                type: 'autocomplete',
                value: preselectValues?.templatePreselect,
                config: {
                    entity: 'template',
                    entityParams: {
                        context: remoteWork ? 'remote_work' : 'employee_vacations',
                    },
                },
            },
            {
                key: 'type',
                name: 'type',
                type: 'select',
                config: {
                    options: this.workdayExceptionReasons
                        .filter((reason) => (reason.type === 'remote_work') === remoteWork)
                        .map((item) => ({ ...item, id: item.type })),
                    key: 'id',
                },
            },
            {
                name: 'unpaid',
                key: 'unpaid',
                type: 'checkbox',
                config: {},
            },
            {
                name: 'comments',
                key: 'comments',
                type: 'string',
                config: {},
            },
        ];

        this.Dialog.open(InputDialogComponent, {
            header: 'new_vacation_request',
            mainField: {
                name: 'from',
                key: 'startDate',
                type: 'date',
                config: {},
                value: moment().add(1, 'd').format(),
            },
            parameters,
        }).then(
            (result: {
                startDate: date;
                duration: number;
                template: number;
                unpaid: boolean;
                type: string;
                comments: string;
            }) => {
                this.Entity.get('employee_contract')
                    .vacationRequest({ ...result, employeeId: this.personEmployee.id })
                    .then((response: { id: number; file_id: number }) => {
                        this.refreshTimestamp();
                        this.Entity.get('file')
                            .get({ id: response.file_id })
                            .then((response) => {
                                this.FilePreview.show(response);
                            });
                    });
            }
        );
    };

    loadEmployeeWorkshifts = () => {
        return this.employeeWorkshiftEntity.search({ id: this.personEmployee.id }).then((response: any) => {
            let employeeWorkshifts: any = { odd: [], even: [] };

            response.forEach((employeeWorkshift: any) => {
                let week = employeeWorkshift.oddWeek ? employeeWorkshifts.odd : employeeWorkshifts.even;

                week[employeeWorkshift.weekday] = employeeWorkshift;
            });

            this.personEmployee.workshifts = { odd: [], even: [] };

            for (let key in employeeWorkshifts) {
                const week = employeeWorkshifts[key];

                week.forEach((employeeWorkshift: any) => {
                    employeeWorkshift.name = this.weekdays[employeeWorkshift.weekday - 1];

                    if (employeeWorkshift.workshift)
                        employeeWorkshift.workshift = findById(this.workshifts, employeeWorkshift.workshift);
                    this.personEmployee.workshifts[key].push(employeeWorkshift);
                });
            }
        });
    };

    onYearMonthChange = (data: any) => {
        this.date.year(data.year).month(data.month);
        this.timestamp = new Date().getTime();

        if (
            !(
                this.scheduleEntries[this.date.format('YYYY-MM-DD')] &&
                this.scheduleEntries[cloneDeep(this.date).add(2, 'M').format('YYYY-MM-DD')]
            )
        )
            this.loadEntries();
    };

    loadEntries = () => {
        return this.Entity.get('employee_schedule_entry')
            .search({
                'employee.id': this.personEmployee.id,
                date: this.QueryExpression.dateRange(
                    moment(this.date).startOf('month'),
                    moment(this.date).endOf('month')
                ),
                partialJoin: {
                    reason: ['id', 'name', 'color'],
                    workshift: ['id', 'name', 'color'],
                },
                sort: { date: 'asc' },
            })
            .then((entries: any) => {
                entries.forEach((entry: any) => (this.scheduleEntries[entry.date] = entry));
                this.ready = true;
                this.refreshTimestamp();
            });
    };

    getDayText = (day: any) => {
        const entry = this.getEntry(day);
        let text = '-';

        if (entry) {
            if (entry.reason) text = entry.reason.name;
            else if (entry.workshift) text = entry.workshift.name;
            else if (entry.isChangedTime) text = this.calendarOptions.changedScheduleColor;
        }

        return text;
    };

    refreshTimestamp = () => (this.timestamp = new Date().getTime());

    handleTemplateAction(template: Template) {
        switch (template.context) {
            case 'remote_work':
                this.createVacationRequest(true, { templatePreselect: template });
                break;

            case 'employee_vacations':
                this.createVacationRequest(false, { templatePreselect: template });
                break;

            default:
                this.Toast.pop('warn', 'unsupported_template', this.Filter.translate('contact_support'));
                break;
        }
    }

    checkEntryInfo = (value: any) => {
        this.Entity.get('employee_schedule_entry')
            .get({
                'employee.id': this.personEmployee.id,
                date: `${value.year}-${++value.month}-${value.day}`,
                join: ['workshift', 'reason'],
                partialJoin: { employee: ['id', 'name'] },
            })
            .then((responseArr: EmployeeScheduleEntry) => {
                const response = responseArr[0];
                let responseHtml = `<h3>${response.date}</h3><br>`;
                if (response.reason) {
                    responseHtml += `<p><b>${response.reason.name}</b></p><br><p>${this.Filter.translate(
                        'paid_by_employer'
                    )}: ${response.reason.paidByEmployer ? '&check;' : '&cross;'}</p>`;
                }
                if (response.workshift) {
                    responseHtml += `<p><b>${response.workshift.name}</b></p><br><p>${moment
                        .utc(response.workshift.start, 'HH:mm:ss')
                        .local()
                        .format('HH:mm:ss')} - ${moment
                        .utc(response.workshift.end, 'HH:mm:ss')
                        .local()
                        .format('HH:mm:ss')}</p>`;
                }

                this.Dialog.open(TextPreviewDialogComponent, {
                    disableCopy: true,
                    disablePrint: true,
                    type: 'html',
                    text: responseHtml,
                });
            });
    };
}
